import React, {useEffect, useState} from "react";
import useAxios from "../../../utils/useAxios";
import {ParticipantPickerPanel} from "../../../components/CustomModel/ParticipantPickerPanel";
import {t} from "i18next";
import FeatherIcon from "feather-icons-react";
import {useIsWideViewport} from "../../../hooks/useIsWideViewport";
import { normalizeTeamPlayers } from "../../../utils/normalizeTeamPlayers";

const GridSelectPlayerModal = ({
                                   tournamentId,
                                   rating,
                                   isOpen,
                                   onClose,
                                   onSuccess,
                                   tournamentGrade,
                                   matchId,
                                   side,
                                   participants,
                                   drawPlayers,
                                   occupiedParticipants = [],
                                   onAddToMatch,
                                   isAlreadyInMatch,
                                   updateMatches,
                                   pickedCandidate,
                                   onPickCandidate,
                                   setLoadingCells
                               }) => {

    const api = useAxios();
    const isWide = useIsWideViewport();
    const [query, setQuery] = useState("");
    // This component stays mounted across opens/closes (the parent just
    // toggles `isOpen`, see the early return below), so the search input's
    // own state would otherwise carry a previous search over into the next
    // open - clear it on every open, not close, so it doesn't flash empty
    // while the panel is still closing.
    useEffect(() => {
        if (isOpen) setQuery("");
    }, [isOpen]);
    // A Set of in-flight candidate keys, not one global boolean - on wide
    // viewports the panel stays open across multiple picks (see onClose
    // below), so a second pick needs to be submittable while the first is
    // still in flight instead of the whole list locking up until it resolves.
    const [submittingIds, setSubmittingIds] = useState(() => new Set());

    const getParticipantKey = (participant, Bye) => {
        if (Bye) return "__bye__";
        return String(tournamentGrade === 1 ? (participant.player_id || participant.id) : (participant.team_id || participant.id));
    };

    // `/draw-stage/{id}/participants/` names the team's own rating `rating`
    // (confirmed live) - NOT `team_rating`, that's a /teams/-endpoint-only
    // name (TournamentPlayers.js/PlayersGridView.js both read it from
    // there). Checking `team_rating` first/alone here always missed,
    // falling through to summing `players[].rating` - which this endpoint's
    // own nested players don't carry either (id/first_name/last_name only),
    // so every pair/team showed 0 regardless of its real rating.
    //
    // Newly created team/pair records can still come back from the backend
    // with their OWN rating at 0 until a later recalculation fills it in
    // (same root cause as backend-tasks/2026-09-12-copied-tournament-team-rating-zero.md),
    // while each member player's own `rating` is already correct straight
    // away - summing those stays as the last-resort fallback for that case.
    const getDisplayRating = (participant) => {
        if (tournamentGrade === 1) return participant?.rating || 0;
        // One rating per slot: a pair carrying a third member (duplicate
        // `sorting`, see src/utils/normalizeTeamPlayers.js) summed three
        // ratings and showed a pair as stronger than it is.
        return participant?.team_rating ?? participant?.rating ??
            normalizeTeamPlayers(participant?.players, tournamentGrade === 2 ? 2 : 0)
                .reduce((acc, p) => acc + (p?.rating || 0), 0);
    };

    // No specific slot targeted (either never had one, or the previous
    // target was just filled and cleared by the parent's onSuccess - see
    // TournamentGrids.js) - a candidate/Bye click here can't post anywhere
    // yet, so it just "picks" it instead: highlighted in this list, "Drop
    // here" on every empty round-1 slot on screen, until a slot is tapped.
    const hasTarget = matchId != null && side != null;

    const handleSelect = async (participant = null, Bye = false) => {
        if (!hasTarget) {
            onPickCandidate?.({ participant, isBye: Bye });
            return;
        }

        const key = getParticipantKey(participant, Bye);
        if (submittingIds.has(key)) return;
        setSubmittingIds(prev => new Set(prev).add(key));
        const cellKey = `${matchId}_${side}`;
        setLoadingCells?.(prev => ({ ...prev, [cellKey]: true }));
        try {
            let payload;

            if (Bye) {
                payload = side === 1 ? { x1: true } : { x2: true };
            } else if (tournamentGrade === 1) {
                payload = side === 1
                    ? { player_1: participant.player_id }
                    : { player_2: participant.player_id };
            } else if (tournamentGrade === 2) {
                if (side === 1) {
                    payload = {
                        team_1: participant.team_id,
                        player_1: participant.players[0].id,
                        player_3: participant.players[1].id
                    };
                } else {
                    payload = {
                        team_2: participant.team_id,
                        player_2: participant.players[0].id,
                        player_4: participant.players[1].id
                    };
                }
            } else if (tournamentGrade === 3) {
                payload = side === 1
                    ? { team_1: participant.team_id }
                    : { team_2: participant.team_id };
            }


            const response = await api.patch(`/api/v1.0/tournaments/${tournamentId}/draws/matches/${matchId}/`, payload);
            // ParticipantPickerPanel is a plain blocking modal on every
            // viewport now (reverted off its old wide-only docked/non-
            // blocking mode) - closing after a successful add is the only
            // feedback that anything happened at all, same as it always was
            // on narrow.
            onClose();
            // Each guarded on its own, independently of the other - this
            // mirrors Groups' own select-participant modal, which only ever
            // does the plain local update below (no server-echo merge) and
            // reliably updates its cell. Grids additionally tries to merge
            // the server's own echoed match object (updateMatches) for
            // freshest data (sets, etc.) - if that merge throws on a
            // response shape it doesn't expect, it must not take the
            // simple, always-correct local optimistic update (onAddToMatch)
            // down with it, or the cell is left showing nothing even though
            // the participant really was saved.
            try {
                if (response?.data?.updated_matches) {
                    updateMatches(response.data.updated_matches, true);
                }
            } catch (e) {
                console.error("Failed to merge server match update:", e);
            }
            try {
                onAddToMatch?.(participant, Bye, matchId, side);
            } catch (e) {
                console.error("Failed to apply local match update:", e);
            }
            // Was destructured but never actually called - the parent's
            // own onSuccess (TournamentGrids.js) relies on this to clear
            // the just-consumed target, which is what flips subsequent
            // candidate clicks into the pick-then-place flow.
            onSuccess?.(participant, Bye, matchId, side);
        } catch (e) {
            console.error("Failed to add participant:", e);
        } finally {
            setSubmittingIds(prev => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
            setLoadingCells?.(prev => {
                const next = { ...prev };
                delete next[cellKey];
                return next;
            });
        }
    };

    if (!isOpen) return null;

    const filterParticipant = (participant) => {
        const q = query.trim().toLowerCase();
        if (!q) return true;

        if (tournamentGrade === 1) {
            return (
                (participant.first_name?.toLowerCase() || "").includes(q) ||
                (participant.last_name?.toLowerCase() || "").includes(q)
            );
        }

        if (tournamentGrade === 2) {
            return participant.players?.some(player =>
                (player.first_name?.toLowerCase() || "").includes(q) ||
                (player.last_name?.toLowerCase() || "").includes(q)
            );
        }

        return (participant.name?.toLowerCase() || "").includes(q);
    };

    const isAlreadyInDraw = (participant) => {
        if (tournamentGrade === 1) {
            const pId = participant.player_id || participant.id;
            if (drawPlayers && drawPlayers.some(id => String(id) === String(pId))) return true;
            if (occupiedParticipants && occupiedParticipants.some(id => String(id) === String(pId))) return true;
            return false;
        }

        if (tournamentGrade === 2) {
            const teamId = participant.team_id || participant.id;
            if (drawPlayers && drawPlayers.some(id => String(id) === String(teamId))) return true;
            if (occupiedParticipants && occupiedParticipants.some(id => String(id) === String(teamId))) return true;
            const p1 = participant.players?.[0]?.id || participant.players?.[0]?.player_id;
            const p2 = participant.players?.[1]?.id || participant.players?.[1]?.player_id;
            if (occupiedParticipants && (
                (p1 && occupiedParticipants.some(id => String(id) === String(p1))) ||
                (p2 && occupiedParticipants.some(id => String(id) === String(p2)))
            )) return true;
            return false;
        }

        if (tournamentGrade === 3) {
            const teamId = participant.team_id || participant.id;
            if (drawPlayers && drawPlayers.some(id => String(id) === String(teamId))) return true;
            if (occupiedParticipants && occupiedParticipants.some(id => String(id) === String(teamId))) return true;
            return false;
        }

        return false;
    };

    const getFilteredParticipants = () => {
        if (!participants?.length) return [];
        // 1. Deduplicate participants pool by unique ID
        const seen = new Set();
        const uniqueParticipants = [];
        for (const p of participants) {
            const uid = tournamentGrade === 1
                ? (p.player_id || p.id)
                : (p.team_id || p.id);
            const key = uid ? String(uid) : JSON.stringify(p);
            if (!seen.has(key)) {
                seen.add(key);
                uniqueParticipants.push(p);
            }
        }

        return uniqueParticipants
            .filter(p => !isAlreadyInDraw(p))
            .filter(filterParticipant);
    };

    const filteredParticipants = getFilteredParticipants();

    // Passed to ParticipantPickerPanel's `header` prop (see that component
    // and ScheduleSelectMatchModal.js, which this mirrors) so this title/
    // search block lives in the docked panel's own fixed top zone next to
    // the close X - same header/padding treatment as the schedule picker,
    // instead of scrolling away with the participant list underneath it.
    const header = (
        <div className="modal-sticky-header">
            <h2>
                <FeatherIcon icon="user"/> {t("Choose participant")}
            </h2>

            <div className="form-group" style={{position: "relative"}}>
                <FeatherIcon icon="search" size={16} className="search-icon"/>
                <input
                    type="text"
                    className="search"
                    placeholder={
                        tournamentGrade === 3
                            ? t("Search participants by team name")
                            : t("Search participants by first name, last name")
                    }
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{paddingLeft: "30px"}}
                />
            </div>
        </div>
    );

    return (
        <ParticipantPickerPanel isOpen={isOpen} onClose={onClose} icon="user" centered header={header}>
            <form className="modal-form">
                <ul className="user-search-results players">
                    {!isAlreadyInMatch && (
                        <li key="bye" onClick={() => handleSelect(null, true)} style={{ cursor: "pointer", opacity: (pickedCandidate?.isBye || submittingIds.has("__bye__")) ? 0.5 : 1 }}>
                            <div>
                                <span className="uppercase">Bye</span>
                            </div>
                        </li>
                    )}

                    {filteredParticipants.map((player, index) => {
                        const isThisPicked = pickedCandidate && !pickedCandidate.isBye && getParticipantKey(pickedCandidate.participant, false) === getParticipantKey(player, false);
                        // Dimmed in place (not removed from the list) while
                        // the add request for this exact participant is in
                        // flight - a full-screen narrow modal has nothing
                        // else on screen to show that the tap registered, so
                        // the row vanishing outright (the old behavior) read
                        // as "nothing happened" for however long the request
                        // took.
                        const isSubmitting = submittingIds.has(getParticipantKey(player, false));
                        return (
                        <li key={index}
                            onClick={() => handleSelect(player)}
                            style={{ display: "grid", gridTemplateColumns: rating ? "40px 1fr" : "1fr", cursor: isSubmitting ? "default" : "pointer", opacity: (isThisPicked || isSubmitting) ? 0.5 : 1, pointerEvents: isSubmitting ? "none" : "auto" }}
                        >
                            {
                                rating &&
                                <div>
                                    <div className="tournament-players" style={{ color: "#595664"}}>
                                        { getDisplayRating(player) }
                                    </div>
                                </div>
                            }
                            <div>
                                {
                                    tournamentGrade === 1 ? (
                                        <>
                                            {player?.first_name && <>{player.first_name[0]}. </>}<span>{player?.last_name}</span>
                                        </>
                                    ) : tournamentGrade === 2 ? (
                                        <>
                                            {player?.players?.[0]?.first_name && ` ${player.players[0].first_name}. `}<span>{player?.players?.[0]?.last_name}</span><br />
                                            {player?.players?.[1]?.first_name && ` ${player.players[1].first_name}. `}<span>{player?.players?.[1]?.last_name}</span>
                                        </>
                                    ) : (
                                        <>
                                            {player?.name}
                                        </>
                                    )
                                }
                            </div>
                        </li>
                        );
                    })}
                </ul>
            </form>
        </ParticipantPickerPanel>
    );
};

export default GridSelectPlayerModal;
